/**
 * Push Template Modal
 *
 * A template carries TWO app identifiers and they are not interchangeable:
 *  - appID       (int)    — the AppRegistry id of the backend SERVICE that owns the template.
 *  - mobileAppId (string) — the constellation appId of the MOBILE app it targets ("ccms.task").
 *
 * Only the second one routes a push: it selects the APNs topic (bundle id). Picking it here lets
 * Compose/Bulk Send pre-select the right app when the template is chosen.
 */

import { useEffect, useState } from 'react';
import { useForm } from '@mantine/form';
import { IconTemplate } from '@tabler/icons-react';
import { Stack, Group, Button, Text, TextInput, NumberInput, Switch, Textarea, Modal, Paper, Badge, Select, Alert } from '../../../../../components/ui';
import { useApplicationCatalogueService, type AppManifest } from '@actschurch/ccms-sdk';
import type { PushTemplateModalProps } from '../types';
import { decodePushBody, parsePushSpec } from '../types';

export function PushTemplateModal({ opened, onClose, template, mode, onSave, loading }: PushTemplateModalProps) {
  const isViewMode = mode === 'view';
  const isCreateMode = mode === 'create';
  const catalogueService = useApplicationCatalogueService();
  const [catalogue, setCatalogue] = useState<AppManifest[]>([]);

  const form = useForm({
    // TemplateID and AppID are assigned by hand — the column doesn't auto-generate — so a create must
    // collect them. They used to be absent from the form entirely, which is why create wrote id 0.
    initialValues: {
      templateID: '' as number | string,
      appID: '' as number | string,
      templateName: '',
      body: '',
      isActive: true,
      mobileAppId: '' as string,
    },
    validate: {
      templateID: (value) =>
        isCreateMode && !(Number(value) > 0) ? 'Template ID is required (assigned manually)' : null,
      appID: (value) => (!(Number(value) > 0) ? 'App ID is required' : null),
      templateName: (value) => (!value || value.trim() === '' ? 'Name required' : null),
      body: (value) => (!value || value.trim() === '' ? 'Message required' : null),
    },
  });

  // Apps come from the Application Catalogue so the list never drifts from what actually ships.
  useEffect(() => {
    if (!opened) return;
    let active = true;
    (async () => {
      try {
        // /catalogue/all is admin-only; fall back to the entitlement-filtered catalogue if refused.
        let apps: AppManifest[] = [];
        try {
          apps = await catalogueService.getAllApps();
        } catch {
          apps = await catalogueService.getCatalogue();
        }
        if (active) setCatalogue(apps || []);
      } catch {
        if (active) setCatalogue([]);
      }
    })();
    return () => { active = false; };
  }, [opened, catalogueService]);

  useEffect(() => {
    if (template) {
      form.setValues({
        templateID: template.templateID,
        appID: template.appID ?? '',
        templateName: template.templateName || '',
        body: decodePushBody(template.body),
        isActive: template.isActive ?? true,
        mobileAppId: template.mobileAppId || '',
      });
    } else if (isCreateMode) form.reset();
  }, [template, isCreateMode]);

  const appName = (appId?: string | null) => {
    if (!appId) return null;
    const app = catalogue.find((a) => a.appId?.toLowerCase() === appId.toLowerCase());
    return app?.displayName || app?.shortName || appId;
  };

  const handleSubmit = async (values: typeof form.values) => {
    if (onSave) {
      await onSave({
        ...values,
        templateID: Number(values.templateID) || 0,
        appID: Number(values.appID) || 0,
        // Empty select → null, so "not linked to an app" is stored as null rather than "".
        mobileAppId: values.mobileAppId || null,
      } as any);
      form.reset();
      onClose();
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={() => { form.reset(); onClose(); }}
      title={isCreateMode ? 'Create Template' : mode === 'edit' ? 'Edit Template' : 'View Template'}
      size="lg"
    >
      {isViewMode && template ? (
        <Stack gap="md">
          <Group gap="xs">
            <Badge variant="light" color="blue">Template #{template.templateID}</Badge>
            <Badge variant="light" color="grape">App {template.appID ?? '—'}</Badge>
            {template.mobileAppId ? (
              <Badge variant="light" color="teal">{appName(template.mobileAppId)}</Badge>
            ) : (
              <Badge variant="light" color="gray">No mobile app</Badge>
            )}
            <Badge variant="light" color={template.isActive ? 'green' : 'gray'}>
              {template.isActive ? 'Active' : 'Inactive'}
            </Badge>
          </Group>
          <div><Text fw={500} size="sm">Name</Text><Text size="sm">{template.templateName}</Text></div>
          <div>
            <Text fw={500} size="sm">Mobile app</Text>
            <Text size="sm">
              {template.mobileAppId
                ? `${appName(template.mobileAppId)} · ${template.mobileAppId}`
                : 'Not linked — a push using this template must pick the app itself.'}
            </Text>
          </div>
          {(() => {
            // A template is a notification spec — render what it will actually look like, and keep the
            // raw JSON visible underneath since that is what you edit.
            const decoded = decodePushBody(template.body);
            const spec = parsePushSpec(decoded);
            return (
              <>
                <div>
                  <Text fw={500} size="sm">Notification</Text>
                  <Paper p="md" withBorder>
                    {spec ? (
                      <Stack gap={4}>
                        <Text fw={600}>{spec.title || '(No title)'}</Text>
                        <Text size="sm">{spec.body || '(No body)'}</Text>
                        {!!spec.actions?.length && (
                          <Group gap="xs" mt={4}>
                            {spec.actions.map((a) => (
                              <Badge key={a.action} size="sm" variant="light">{a.title}</Badge>
                            ))}
                          </Group>
                        )}
                      </Stack>
                    ) : (
                      <Text>{decoded || '(No Message)'}</Text>
                    )}
                  </Paper>
                </div>
                {spec && (
                  <div>
                    <Text fw={500} size="sm">Template JSON</Text>
                    <Paper p="md" withBorder>
                      <Text size="xs" style={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>{decoded}</Text>
                    </Paper>
                  </div>
                )}
              </>
            );
          })()}
          <Group justify="space-between">
            <Button variant="subtle" onClick={onClose}>Close</Button>
            {onSave && <Button variant="light" leftSection={<IconTemplate size={16} />} onClick={onClose}>Edit</Button>}
          </Group>
        </Stack>
      ) : (
        <form onSubmit={form.onSubmit(handleSubmit)}>
          <Stack gap="md">
            {isCreateMode && (
              <Alert color="blue" p="xs">
                <Text size="xs">
                  Template ID and App ID are assigned manually — they are not generated. Pick an ID that
                  is free and inside the owning app's range (e.g. ACCounselling uses 57000+).
                </Text>
              </Alert>
            )}

            <Group grow>
              <NumberInput
                label="Template ID"
                description={isCreateMode ? 'Must be unique' : 'Cannot be changed'}
                placeholder="e.g. 57012"
                required
                min={1}
                allowDecimal={false}
                disabled={!isCreateMode}
                {...form.getInputProps('templateID')}
              />
              <NumberInput
                label="App ID"
                description="Owning service (AppRegistry id, e.g. 34)"
                placeholder="e.g. 34"
                required
                min={1}
                allowDecimal={false}
                {...form.getInputProps('appID')}
              />
            </Group>

            <TextInput label="Template Name" placeholder="e.g., Order Confirmation" required {...form.getInputProps('templateName')} />

            <Select
              label="Mobile app (optional)"
              description="The app this template notifies. It selects the APNs topic, and Compose/Bulk Send pre-select it when this template is chosen."
              placeholder="Not linked to an app"
              data={catalogue
                .filter((a) => a.enabled !== false)
                .map((a) => ({ value: a.appId, label: `${a.displayName || a.shortName || a.appId} · ${a.appId}` }))}
              value={form.values.mobileAppId || null}
              onChange={(v) => form.setFieldValue('mobileAppId', v || '')}
              searchable
              clearable
              nothingFoundMessage="No apps in the catalogue"
            />

            <Textarea label="Message" placeholder="Template message..." required rows={6} {...form.getInputProps('body')} />
            <Switch label="Active" description="Only active templates can be used" {...form.getInputProps('isActive', { type: 'checkbox' })} />
            <Group justify="flex-end" gap="xs">
              <Button variant="subtle" onClick={onClose}>Cancel</Button>
              <Button type="submit" loading={loading} variant="light">{isCreateMode ? 'Create' : 'Save'}</Button>
            </Group>
          </Stack>
        </form>
      )}
    </Modal>
  );
}
